import { DEFAULT_THEME, type ThemeTokens } from '@/shared/config/theme/default-theme';
import { readMode, resolveMode, type ResolvedMode } from '@/shared/config/theme/mode';

/**
 * Turns a theme document into CSS custom properties on `:root` (§7.2).
 *
 * Every colour in the panel resolves through these variables, so a theme activated in
 * the dashboard changes the whole UI with no rebuild — which is the entire point of
 * §7. A component that hardcodes a hex is a defect (§5.7).
 */
export function applyTheme(tokens: ThemeTokens, mode: ResolvedMode = resolveMode(readMode())): void {
  const root = document.documentElement;

  // ⚠️ Falls back to the dark palette when the theme carries no daylight one, rather than
  // inverting it. §7's tokens are semantic: an inverted `danger` is a colour nobody chose and no
  // contrast rule has ever checked. A theme without daylight does not offer daylight.
  const colors = (mode === 'light' ? tokens.colorsLight : undefined) ?? tokens.colors;

  for (const [name, value] of Object.entries(colors)) {
    root.style.setProperty(`--color-${kebab(name)}`, value);
  }

  // ⚠️ Told to the browser as well as painted. Without it the scrollbars, the form controls and
  // the caret stay in the other scheme — the small parts nobody styles, which are exactly the
  // parts that give away a half-done theme.
  root.style.colorScheme = mode;
  root.dataset['theme'] = mode;

  for (const [name, px] of Object.entries(tokens.typography.scale)) {
    root.style.setProperty(`--text-${name}`, `${px / 16}rem`);
  }

  for (const [name, px] of Object.entries(tokens.radius)) {
    root.style.setProperty(`--radius-${name}`, `${px}px`);
  }

  root.style.setProperty('--font-sans', fontStack(tokens.typography.fontFamily));

  const unit = tokens.spacing['unit'] ?? 4;
  root.style.setProperty('--spacing', `${unit}px`);
}

/**
 * ⚠️ The theme names a font family; it does not deliver one.
 *
 * One family is self-hosted and will therefore actually render: IBM Plex Sans Arabic,
 * the default (`src/app/fonts.css`). §12.12 forbids fetching a face from a third-party
 * host, so any OTHER family a theme names resolves only if the operator's machine
 * happens to have it — and the fallbacks below are what renders when it does not.
 *
 * That is the honest limit of §7 for typography: colours, sizes and radii travel in the
 * theme document, but a typeface is a file, and a file the panel is forbidden to fetch
 * from elsewhere has to be in the build. Changing the family is a deployment, not a
 * dashboard edit — which is why the stack still has fallbacks rather than assuming the
 * named family exists.
 *
 * Arabic first in the fallback chain: a Latin-first stack renders Arabic through a face
 * that was never designed for it, and the difference is immediately visible in connected
 * letterforms.
 */
function fontStack(preferred: string): string {
  return [
    `"${preferred}"`,
    '"Noto Sans Arabic"',
    '"Segoe UI"',
    'Tahoma',
    'system-ui',
    'sans-serif',
  ].join(', ');
}

/** `surfaceElevated` → `surface-elevated`, so the CSS variable reads like CSS. */
function kebab(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Applies the bundled default immediately, before any network call.
 *
 * §7.3 wants the active theme fetched before first paint, but "before first paint" and
 * "after a round trip" cannot both be true — so the bundled default paints first and
 * the fetched theme replaces it. The alternative is a blank screen while the API
 * answers, which for a control room at 3 a.m. is worse than a palette that is one
 * version behind for 200 ms.
 */
export function applyBundledTheme(mode?: ResolvedMode): void {
  applyTheme(DEFAULT_THEME, mode ?? resolveMode(readMode()));
}
